import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Chip, Button, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Drawer,
  Grid, Divider, Tab, Tabs, CircularProgress, Snackbar, Alert,
  InputAdornment, Select, MenuItem, FormControl, InputLabel,
  Tooltip, LinearProgress, Avatar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, CardContent,
} from '@mui/material';
import {
  Add, Edit, Visibility, Search, LocationOn, History,
  ElectricScooter, BatteryChargingFull, Speed, Close,
  Assignment, AssignmentReturn, Info, Refresh,
  DirectionsCar, Build, CloudUpload, Delete, Restore,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { InvokeApi, apiList, postImg } from '../services/ApiService';
import { Config } from '../config/Config';
import { useNavigate } from 'react-router-dom';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const STATUS_COLOR = { active: 'success', assigned: 'primary', maintenance: 'warning', retired: 'error' };
const STATUS_OPTS = ['active', 'assigned', 'maintenance', 'retired'];

const EMPTY_FORM = {
  vehicle_number: '', 
  chassis_number: '', 
  model: '', 
  color: '',
  battery_pct: '100', 
  battery_capacity: '', 
  battery_health_pct: '100',
  range_km: '',
  year: String(new Date().getFullYear()),
  status: 'active', 
  insurance_expiry: '', 
  rc_expiry: '', 
  odometer: '', 
  notes: '',
  device_imei: '', 
  vehicle_name: '',
  vehicle_image_file: null,
  charging_port_type: '',
  max_charging_rate_kw: '',
};

const battColor = (pct) => {
  const v = parseInt(pct ?? 0);
  if (v >= 60) return '#16a34a';
  if (v >= 30) return '#d97706';
  return '#dc2626';
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const fmtTime = (t) => {
  if (!t) return '—';
  try {
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    return `${(hour % 12) || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
  } catch { return t; }
};

// ── Info row ───────────────────────────────────────────────────────────────────
const InfoTable = ({ rows }) => (
  <Box sx={{ mb: 1 }}>
    {rows.filter(([, v]) => v != null && v !== '').map(([label, value]) => (
      <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.9, borderBottom: '1px solid #f1f5f9' }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', maxWidth: '55%', wordBreak: 'break-word' }}>
          {value ?? '—'}
        </Typography>
      </Box>
    ))}
  </Box>
);

// ── Stat card ──────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color = '#1a237e' }) => (
  <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 2.5, display: 'flex', gap: 2, alignItems: 'center' }}>
    <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {React.cloneElement(icon, { sx: { color, fontSize: 26 } })}
    </Box>
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a237e', lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
    </Box>
  </Card>
);

// ── Section label ──────────────────────────────────────────────────────────────
const SectionLabel = ({ text, mt }) => (
  <Typography variant="subtitle2" sx={{
    fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
    fontSize: 11, letterSpacing: 0.8, mb: 1, mt: mt ? 2.5 : 0,
  }}>
    {text}
  </Typography>
);

// ── Form field shorthand — defined OUTSIDE component to prevent remount on each keystroke ──
const FField = ({ k, form, setF, label, type = 'text', half = true, required = false, multiline = false }) => (
  <Grid item xs={12} sm={half ? 6 : 12}>
    <TextField
      fullWidth size="small" label={label} type={type} required={required}
      value={form[k] ?? ''} onChange={e => setF(k, e.target.value)}
      multiline={multiline} rows={multiline ? 3 : 1}
      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
    />
  </Grid>
);
  
export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Detail drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [vehicleDetail, setVehicleDetail] = useState(null);
  const [gpsData, setGpsData] = useState(null);
  const [history, setHistory] = useState([]);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  // ── Fetch all vehicles once ─────────────────────────────────────────────────
  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    const res = await InvokeApi(Config.base_url + apiList.getVehicles, {
      page: 1, per_page: 1000, search: '', status: '', // Increased to 1000
    });
    if (res?.code === '200') {
      const list = (res.vehicles ?? []).map((r, i) => ({ ...r, id: r.id ?? i + 1 }));
      setVehicles(list);
      setTotal(res.total ?? list.length);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  // ── Local filter (search + status) ─────────────────────────────────────────
  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      vehicles.filter((v) => {
        const matchSearch =
          !q ||
          v.vehicle_number?.toLowerCase().includes(q) ||
          v.model?.toLowerCase().includes(q) ||
          v.chassis_number?.toLowerCase().includes(q) ||
          v.assigned_user?.toLowerCase().includes(q);
        const matchStatus = !statusFilter || v.status === statusFilter;
        return matchSearch && matchStatus;
      })
    );
    setPage(0);
  }, [search, statusFilter, vehicles]);

  // ── Stats ────────────────────────────────────────────────────────────────────
  const stats = {
    total: vehicles.length,
    active: vehicles.filter(v => v.status === 'active').length,
    assigned: vehicles.filter(v => v.status === 'assigned').length,
    maintenance: vehicles.filter(v => v.status === 'maintenance').length,
  };

  const paginated = filtered.slice(page * pageSize, page * pageSize + pageSize);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openAdd = () => { setEditVehicle(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditVehicle(row);
    setForm({
      ...EMPTY_FORM, 
      ...row,
      vehicle_name: row.vehicle_name ?? '',
      battery_health_pct: row.battery_health_pct ?? '100',
      charging_port_type: row.charging_port_type ?? '',
      max_charging_rate_kw: row.max_charging_rate_kw ?? '',
      insurance_expiry: row.insurance_expiry ?? '',
      rc_expiry: row.rc_expiry ?? '',
      odometer: row.odometer ?? '',
      notes: row.notes ?? '',
      device_imei: row.device_imei ?? '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.vehicle_number.trim()) { setSnack({ open: true, msg: 'Vehicle number is required.', sev: 'error' }); return; }
    if (!form.chassis_number.trim()) { setSnack({ open: true, msg: 'Chassis number is required.', sev: 'error' }); return; }
    if (!form.model.trim())          { setSnack({ open: true, msg: 'Model is required.', sev: 'error' }); return; }
    setSaving(true);
    const endpoint = editVehicle ? apiList.editVehicle : apiList.addVehicle;
    const payload  = editVehicle ? { ...form, id: editVehicle.id } : form;
    // Remove file from payload (will be uploaded separately)
    const { vehicle_image_file, ...dataPayload } = payload;
    const res = await InvokeApi(Config.base_url + endpoint, dataPayload);
    
    if (res?.code === '200') {
      // If there's an image file, upload it
      if (form.vehicle_image_file) {
        const vehicleId = editVehicle ? editVehicle.id : res.id;
        const formData = new FormData();
        formData.append('image', form.vehicle_image_file);
        formData.append('id', vehicleId);
        await postImg(Config.base_url + apiList.uploadVehicleImage, formData);
      }
      
      setSnack({ open: true, msg: editVehicle ? 'Vehicle updated successfully.' : 'Vehicle added successfully.', sev: 'success' });
      setDialogOpen(false);
      fetchVehicles();
    } else {
      setSnack({ open: true, msg: res?.message || 'Save failed. Please try again.', sev: 'error' });
    }
    setSaving(false);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Are you sure you want to retire vehicle ${row.vehicle_number}? This will change its status to "retired".`)) {
      return;
    }
    
    const res = await InvokeApi(Config.base_url + apiList.deleteVehicle, { id: row.id });
    if (res?.code === '200') {
      setSnack({ open: true, msg: res.message || 'Vehicle retired successfully.', sev: 'success' });
      fetchVehicles();
    } else {
      setSnack({ open: true, msg: res?.message || 'Failed to retire vehicle.', sev: 'error' });
    }
  };

  const handleRestore = async (row) => {
    if (!window.confirm(`Are you sure you want to restore vehicle ${row.vehicle_number}? This will change its status to "active".`)) {
      return;
    }
    
    const res = await InvokeApi(Config.base_url + apiList.restoreVehicle, { id: row.id });
    if (res?.code === '200') {
      setSnack({ open: true, msg: res.message || 'Vehicle restored successfully.', sev: 'success' });
      fetchVehicles();
    } else {
      setSnack({ open: true, msg: res?.message || 'Failed to restore vehicle.', sev: 'error' });
    }
  };

  const handleView = (row) => {
    navigate(`/vehicle/${row.id}`);
  };

  // ── Form field shorthand uses the top-level FField with form/setF props ──────

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Vehicles</Typography>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} vehicle{filtered.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchVehicles()} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, px: 3 }}
          >
            Add Vehicle
          </Button>
        </Box>
      </Box>

      {/* ── Stat cards ──────────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><StatCard icon={<ElectricScooter />}    label="Total Vehicles"  value={stats.total}       color="#1a237e" /></Grid>
        <Grid item xs={6} sm={3}><StatCard icon={<BatteryChargingFull />} label="Available"      value={stats.active}      color="#16a34a" /></Grid>
        <Grid item xs={6} sm={3}><StatCard icon={<DirectionsCar />}      label="Assigned"        value={stats.assigned}    color="#1565c0" /></Grid>
        <Grid item xs={6} sm={3}><StatCard icon={<Build />}              label="In Maintenance"  value={stats.maintenance} color="#d97706" /></Grid>
      </Grid>

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search vehicle no, chassis, model, rider..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 260 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTS.map(s => (
                <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchVehicles()} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </CardContent>
      </Card>

      {/* ── Table ───────────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['#', 'Vehicle', 'Chassis', 'Model', 'Battery', 'GPS Status', 'Status', 'Assigned To', 'Assigned Date', 'Actions'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No vehicles found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((v) => {
                      const pct = parseInt(v.battery_pct ?? 0);
                      return (
                        <TableRow key={v.id} hover>
                          <TableCell>
                            <Chip label={`#${v.id}`} size="small"
                              sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 12 }} />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ width: 34, height: 34, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 13, fontWeight: 700 }}>
                                {(v.vehicle_number || 'V')[0]}
                              </Avatar>
                              <Box>
                                <Typography fontWeight={700} fontSize={13} sx={{ fontFamily: 'monospace' }}>
                                  {v.vehicle_number}
                                </Typography>
                                <Typography fontSize={11} color="text.secondary">{v.color || '—'}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                              {v.chassis_number || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 13 }}>{v.model || '—'}</TableCell>
                          <TableCell sx={{ minWidth: 110 }}>
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: battColor(pct) }}>{pct}%</Typography>
                              <LinearProgress variant="determinate" value={pct}
                                sx={{ height: 5, borderRadius: 1, mt: 0.4, bgcolor: '#e2e8f0',
                                  '& .MuiLinearProgress-bar': { bgcolor: battColor(pct) } }} />
                            </Box>
                          </TableCell>
                          <TableCell>
                            {v.gps_status === 'active' ? (
                              <Chip 
                                label="Active" 
                                size="small" 
                                sx={{ 
                                  background: '#e8f5e9', 
                                  color: '#2e7d32', 
                                  fontWeight: 600,
                                  '& .MuiChip-icon': { color: '#2e7d32' }
                                }} 
                                icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2e7d32', ml: 0.5 }} />}
                              />
                            ) : (
                              <Chip 
                                label="Offline" 
                                size="small" 
                                sx={{ 
                                  background: '#fff3e0', 
                                  color: '#e65100', 
                                  fontWeight: 600,
                                  '& .MuiChip-icon': { color: '#e65100' }
                                }} 
                                icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#e65100', ml: 0.5 }} />}
                              />
                            )}
                            {v.gps_last_update && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3, fontSize: 10 }}>
                                {new Date(v.gps_last_update).toLocaleString('en-IN', { 
                                  day: '2-digit', 
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={v.status || 'N/A'} color={STATUS_COLOR[v.status] || 'default'} size="small" sx={{ fontWeight: 600, }} />
                          </TableCell>
                          <TableCell>
                            {v.assigned_user
                              ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: '#e8eaf6', color: '#1a237e', fontWeight: 700 }}>
                                    {v.assigned_user[0].toUpperCase()}
                                  </Avatar>
                                  <Box>
                                    <Typography fontSize={12} fontWeight={600}>{v.assigned_user}</Typography>
                                    <Typography fontSize={11} color="text.secondary">{v.assigned_phone || ''}</Typography>
                                  </Box>
                                </Box>
                              : <Typography variant="caption" color="text.secondary">—</Typography>
                            }
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {v.assigned_date ? fmtDate(v.assigned_date) : '—'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => openEdit(v)} sx={{ color: '#1565c0' }}>
                                  <Edit sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Details">
                                <IconButton size="small" onClick={() => handleView(v)} sx={{ color: '#1a237e' }}>
                                  <Visibility sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                              {v.status === 'retired' ? (
                                <Tooltip title="Restore Vehicle">
                                  <IconButton size="small" onClick={() => handleRestore(v)} sx={{ color: '#16a34a' }}>
                                    <Restore sx={{ fontSize: 17 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Retire Vehicle">
                                  <IconButton size="small" onClick={() => handleDelete(v)} sx={{ color: '#dc2626' }}>
                                    <Delete sx={{ fontSize: 17 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 15, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }}
            />
          </>
        )}
      </Card>

      {/* ── Add / Edit Dialog ───────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
          <IconButton size="small" onClick={() => setDialogOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2.5}>

            {/* ── Identity ── */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8 }}>
                Vehicle Identity
              </Typography>
            </Grid>
            <FField k="vehicle_number" form={form} setF={setF} label="Vehicle Number *" required />
            <FField k="vehicle_name" form={form} setF={setF} label="Vehicle Name" />
            <FField k="chassis_number" form={form} setF={setF} label="Chassis Number *" required />
            <FField k="model"          form={form} setF={setF} label="Model *"          required />
            <FField k="color"          form={form} setF={setF} label="Color" />
            <FField k="year"           form={form} setF={setF} label="Year"  type="number" />
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={e => setF('status', e.target.value)}>
                  {STATUS_OPTS.map(s => (
                    <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ── Battery & Performance ── */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8 }}>
                Battery & Performance
              </Typography>
            </Grid>
            <FField k="battery_pct"      form={form} setF={setF} label="Battery Level (%)"      type="number" />
            <FField k="battery_capacity" form={form} setF={setF} label="Battery Capacity (kWh)" type="number" />
            <FField k="battery_health_pct" form={form} setF={setF} label="Battery Health (%)" type="number" />
            <FField k="range_km"         form={form} setF={setF} label="Range (km)"             type="number" />
            <FField k="odometer"         form={form} setF={setF} label="Odometer (km)"          type="number" />

            {/* ── Charging Specifications ── */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8 }}>
                Charging Specifications
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Charging Port Type</InputLabel>
                <Select value={form.charging_port_type || ''} label="Charging Port Type" onChange={e => setF('charging_port_type', e.target.value)}>
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="type1">Type 1</MenuItem>
                  <MenuItem value="type2">Type 2</MenuItem>
                  <MenuItem value="ccs">CCS</MenuItem>
                  <MenuItem value="chademo">CHAdeMO</MenuItem>
                  <MenuItem value="tesla">Tesla</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <FField k="max_charging_rate_kw" form={form} setF={setF} label="Max Charging Rate (kW)" type="number" />

            {/* ── Documents ── */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8 }}>
                Documents & Expiry
              </Typography>
            </Grid>
            <FField k="insurance_expiry" form={form} setF={setF} label="Insurance Expiry"        type="date" />
            <FField k="rc_expiry"        form={form} setF={setF} label="RC / Registration Expiry" type="date" />

            {/* ── Device ── */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8 }}>
                GPS Device
              </Typography>
            </Grid>
            <FField k="device_imei" form={form} setF={setF} label="Device IMEI (15 digits)" half={false} />

            {/* ── Vehicle Image ── */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8, mb: 1 }}>
                Vehicle Image
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {form.vehicle_image_file ? form.vehicle_image_file.name : 'Upload Vehicle Image'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setForm(f => ({ ...f, vehicle_image_file: file }));
                  }}
                />
              </Button>
              {form.vehicle_image_file && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Selected: {form.vehicle_image_file.name}
                </Typography>
              )}
            </Grid>

            {/* ── Notes ── */}
            <Grid item xs={12}><Divider /></Grid>
            <FField k="notes" form={form} setF={setF} label="Admin Notes" half={false} multiline />
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, px: 4, fontWeight: 700 }}
          >
            {saving
              ? <CircularProgress size={18} sx={{ color: '#fff' }} />
              : editVehicle ? 'Update Vehicle' : 'Add Vehicle'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Detail Drawer ───────────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100vw', md: 620 }, display: 'flex', flexDirection: 'column' } }}
      >
        {/* Drawer header */}
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(135deg,#1a237e,#1565c0)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff' }}>
                {selected?.vehicle_number ?? 'Vehicle Details'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.3 }}>
                {selected?.model ?? ''}
                {selected?.color ? ` · ${selected.color}` : ''}
                {selected?.year  ? ` · ${selected.year}`  : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {selected?.status && (
                <Chip label={selected.status} color={STATUS_COLOR[selected.status] || 'default'} size="small" sx={{ fontWeight: 700 }} />
              )}
              <Tooltip title="Edit Vehicle">
                <IconButton size="small" onClick={() => { setDrawerOpen(false); openEdit(selected); }} sx={{ color: '#fff' }}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Tabs value={drawerTab} onChange={(_, v) => setDrawerTab(v)}
            sx={{ px: 2, '& .MuiTab-root': { fontWeight: 700, fontSize: 13 } }}>
            <Tab icon={<Info sx={{ fontSize: 16 }} />}     iconPosition="start" label="Info" />
            <Tab icon={<LocationOn sx={{ fontSize: 16 }} />} iconPosition="start" label="GPS" />
            <Tab icon={<History sx={{ fontSize: 16 }} />}  iconPosition="start" label="History" />
          </Tabs>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
          ) : (
            <>
              {/* ── Tab 0: Info ─────────────────────────────────────────────── */}
              {drawerTab === 0 && vehicleDetail && (
                <Box>
                  {/* Battery gauge card */}
                  <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2.5, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight={700}>Battery Level</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: battColor(vehicleDetail.battery_pct) }}>
                        {vehicleDetail.battery_pct ?? '—'}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={parseInt(vehicleDetail.battery_pct ?? 0)}
                      sx={{ height: 10, borderRadius: 5, bgcolor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: battColor(vehicleDetail.battery_pct) } }}
                    />
                    <Box sx={{ display: 'flex', gap: 3, mt: 1.5, flexWrap: 'wrap' }}>
                      {vehicleDetail.battery_capacity && <Typography variant="caption" color="text.secondary">Capacity: <b>{vehicleDetail.battery_capacity} kWh</b></Typography>}
                      {vehicleDetail.range_km         && <Typography variant="caption" color="text.secondary">Range: <b>{vehicleDetail.range_km} km</b></Typography>}
                      {vehicleDetail.odometer         && <Typography variant="caption" color="text.secondary">Odometer: <b>{vehicleDetail.odometer} km</b></Typography>}
                    </Box>
                  </Card>

                  <SectionLabel text="Vehicle Identity" />
                  <InfoTable rows={[
                    ['Vehicle Number', vehicleDetail.vehicle_number],
                    ['Chassis Number', vehicleDetail.chassis_number],
                    ['Model',          vehicleDetail.model],
                    ['Color',          vehicleDetail.color],
                    ['Year',           vehicleDetail.year],
                    ['Status',         vehicleDetail.status],
                    ['Device IMEI',    vehicleDetail.device_imei],
                  ]} />

                  <SectionLabel text="Current Assignment" mt />
                  {vehicleDetail.assigned_user ? (
                    <Card elevation={0} sx={{ border: '1px solid #bfdbfe', borderRadius: 2, p: 2, bgcolor: '#eff6ff', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Assignment sx={{ color: '#2563eb', fontSize: 20 }} />
                        <Typography variant="body2" fontWeight={700} color="#1e40af">Currently Assigned</Typography>
                      </Box>
                      <InfoTable rows={[
                        ['Assigned To',  vehicleDetail.assigned_user],
                        ['Phone',        vehicleDetail.assigned_phone],
                        ['Assigned By',  vehicleDetail.assigned_by],
                        ['Date',         vehicleDetail.assigned_date],
                        ['Time',         vehicleDetail.assigned_time],
                      ]} />
                    </Card>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>Not currently assigned.</Typography>
                  )}

                  <SectionLabel text="Documents & Expiry" mt />
                  <InfoTable rows={[
                    ['Insurance Expiry', vehicleDetail.insurance_expiry ? fmtDate(vehicleDetail.insurance_expiry) : null],
                    ['RC / Reg Expiry',  vehicleDetail.rc_expiry        ? fmtDate(vehicleDetail.rc_expiry)        : null],
                  ]} />

                  {vehicleDetail.notes && (
                    <>
                      <SectionLabel text="Admin Notes" mt />
                      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2, bgcolor: '#fafafa' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#374151' }}>{vehicleDetail.notes}</Typography>
                      </Card>
                    </>
                  )}

                  <SectionLabel text="Record" mt />
                  <InfoTable rows={[
                    ['Added On',    vehicleDetail.created_at ? new Date(vehicleDetail.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : null],
                    ['Last Updated',vehicleDetail.updated_at ? new Date(vehicleDetail.updated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : null],
                  ]} />
                </Box>
              )}
              {drawerTab === 0 && !vehicleDetail && !detailLoading && (
                <Box sx={{ textAlign: 'center', py: 8, color: '#94a3b8' }}>
                  <Typography>No details available.</Typography>
                </Box>
              )}

              {/* ── Tab 1: GPS ───────────────────────────────────────────────── */}
              {drawerTab === 1 && (
                <Box>
                  {gpsData?.latitude && gpsData?.longitude ? (
                    <>
                      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip icon={<LocationOn />} label="Live Location" color="success" size="small" />
                        {gpsData.speed != null && (
                          <Chip icon={<Speed />} label={`${gpsData.speed} km/h`} size="small" variant="outlined" />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Last updated: {gpsData.updated_at || 'N/A'}
                        </Typography>
                      </Box>
                      <MapContainer
                        center={[parseFloat(gpsData.latitude), parseFloat(gpsData.longitude)]}
                        zoom={15}
                        style={{ height: 440, borderRadius: 12, border: '1px solid #e2e8f0' }}
                      >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <Marker position={[parseFloat(gpsData.latitude), parseFloat(gpsData.longitude)]}>
                          <Popup>
                            <strong>{selected?.vehicle_number}</strong><br />
                            {selected?.model}<br />
                            {gpsData.speed != null ? `Speed: ${gpsData.speed} km/h` : ''}<br />
                            <small>{gpsData.updated_at}</small>
                          </Popup>
                        </Marker>
                      </MapContainer>
                      <Box sx={{ mt: 2 }}>
                        <InfoTable rows={[
                          ['Latitude',     gpsData.latitude],
                          ['Longitude',    gpsData.longitude],
                          ['Speed',        gpsData.speed != null ? `${gpsData.speed} km/h` : null],
                          ['Last Updated', gpsData.updated_at],
                        ]} />
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8, color: '#94a3b8' }}>
                      <LocationOn sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                      <Typography variant="h6" fontWeight={600} color="#64748b">No GPS Data</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        GPS data will appear once the vehicle starts syncing.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* ── Tab 2: History ───────────────────────────────────────────── */}
              {drawerTab === 2 && (
                <Box>
                  {history.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8, color: '#94a3b8' }}>
                      <History sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                      <Typography variant="h6" fontWeight={600} color="#64748b">No History Yet</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Assignment and return events will appear here.
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {history.length} event{history.length !== 1 ? 's' : ''} — most recent first
                      </Typography>
                      {/* Timeline */}
                      <Box sx={{ position: 'relative', pl: 3 }}>
                        <Box sx={{ position: 'absolute', left: 11, top: 0, bottom: 0, width: 2, bgcolor: '#e2e8f0' }} />
                        {history.map((h, i) => {
                          const isAssigned = h.event_type === 'assigned';
                          const dotColor = isAssigned ? '#16a34a' : '#f59e0b';
                          const bgColor  = isAssigned ? '#f0fdf4' : '#fffbeb';
                          const border   = isAssigned ? '#bbf7d0' : '#fde68a';
                          return (
                            <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2.5, position: 'relative' }}>
                              <Box sx={{
                                position: 'absolute', left: -17, top: 14,
                                width: 14, height: 14, borderRadius: '50%',
                                bgcolor: dotColor, border: '2px solid #fff',
                                boxShadow: `0 0 0 2px ${dotColor}40`, zIndex: 1,
                              }} />
                              <Card elevation={0} sx={{ flex: 1, border: `1px solid ${border}`, borderRadius: 2, p: 2, bgcolor: bgColor }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isAssigned
                                      ? <Assignment sx={{ fontSize: 18, color: '#16a34a' }} />
                                      : <AssignmentReturn sx={{ fontSize: 18, color: '#d97706' }} />}
                                    <Typography variant="body2" fontWeight={800}
                                      sx={{ color: isAssigned ? '#15803d' : '#b45309' }}>
                                      {isAssigned ? 'Vehicle Assigned' : 'Vehicle Returned'}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {h.date || ''} {h.time ? `· ${fmtTime(h.time)}` : ''}
                                  </Typography>
                                </Box>
                                <Grid container spacing={1.5}>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Rider</Typography>
                                    <Typography variant="body2" fontWeight={600}>{h.user_name || '—'}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{h.user_phone || '—'}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">
                                      {isAssigned ? 'Assigned By' : 'Collected By'}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>{h.by_name || '—'}</Typography>
                                  </Grid>
                                  {!isAssigned && h.notes && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="text.secondary">Return Notes</Typography>
                                      <Typography variant="body2">{h.notes}</Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </Card>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>

      {/* ── Snackbar ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
